import { FC, useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  CartItemModel,
  CartItemModifierGroupModel,
  CartItemModifierModel,
  StoreModifierGroupModel,
  StoreProductModel,
} from "../../../models";
import { Modal } from "react-bootstrap";
import { formatCurrency } from "../../../utilities";
import { useOrderOption, useStore } from "../../../hooks";
import { useCart } from "../../../hooks";
import { CommonModifierGroup } from "../CommonModifierGroup";
import { CrossSellProduct } from "../CrossSellProduct";
import { TabModifierGroup } from "../TabModifierGroup";
import { PiMinusCircleBold, PiPlusCircleBold } from "react-icons/pi";
import { useProductPrice } from "../../../hooks";
import { ModifierGroupTypeEnum } from "../../../enums";
import { CustomModifierGroup } from "../CustomModifierGroup";
import { QuantityModifierGroup } from "../QuantityModifierGroup";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { MAX_PRODUCT_QUANTITY } from "../../../constants";
import { Toast } from "../../Toast";

type ModifierErrors = {
  [g_id: string]: {
    g_name: string;
    message: string;
  };
};

type Props = {
  mode?: "add" | "edit";
  product: StoreProductModel;
  show: boolean;
  handleSubmit: () => void;
  handleClose: () => void;
  cartProduct?: CartItemModel;
  index?: number;
};

const ProductAddOnModal: FC<Props> = ({
  mode = "add",
  index,
  cartProduct,
  product,
  show,
  handleClose,
  handleSubmit,
}) => {
  const { menuId, storeProducts, hasAdInflation } = useStore();
  const { addToCart, updateCartItem } = useCart();
  const { orderOption } = useOrderOption();

  const [quantity, setQuantity] = useState<number>(
    cartProduct?.product_qty || 1,
  );
  const [instructions, setInstructions] = useState<string>(
    cartProduct?.product_instruction || "",
  );
  // Initialize from cartProduct to avoid first-render gap in edit mode
  const [selectedModifiers, setSelectedModifiers] = useState<
    CartItemModifierGroupModel[]
  >(cartProduct?.modifier_group || []);
  const [errors, setErrors] = useState<ModifierErrors>({});

  // Add refs to track modifier group elements
  const modifierRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const isEditMode = mode === "edit";

  useEffect(() => {
    setErrors({});
    setQuantity(isEditMode ? cartProduct?.product_qty || 1 : 1);
    setInstructions(isEditMode ? cartProduct?.product_instruction || "" : "");
    setSelectedModifiers(isEditMode ? cartProduct?.modifier_group || [] : []);
  }, [show]);

  // Function to scroll to error element
  const scrollToError = (g_id: string) => {
    const element = modifierRefs.current[g_id];
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const handleSelectModifier = useCallback(
    (
      modifierGroup: StoreModifierGroupModel,
      modifier: CartItemModifierModel,
    ) => {
      const isChecked = modifier.selected;

      setErrors((prev) => {
        const { [modifierGroup.g_id]: removed, ...rest } = prev;
        return rest;
      });

      setSelectedModifiers((prev) => {
        // Deep clone previous state
        let updated = prev.map((group) => ({
          ...group,
          modifiers: group.modifiers.map((m) => ({ ...m })),
        }));

        const groupIndex = updated.findIndex(
          (g) => g.g_id === modifierGroup.g_id,
        );

        // Modifier object clone
        const modifierObj = {
          m_id: modifier.m_id,
          m_name: modifier.m_name,
          m_price: Number(modifier.m_price),
          m_qty: Number(modifier.m_qty),
          prices: modifier.prices ? [...modifier.prices] : [],
          selected: modifier.selected,
        };

        // CASE 1 — group exists
        if (groupIndex !== -1) {
          let group = updated[groupIndex];

          const existingIndex = group.modifiers.findIndex(
            (m) => m.m_id === modifier.m_id,
          );

          if (isChecked) {
            // Add or replace modifier
            let newModifiers =
              modifierGroup.g_max_selection === 1
                ? [modifierObj] // only one allowed
                : existingIndex === -1
                  ? [...group.modifiers, modifierObj]
                  : group.modifiers.map((m, idx) =>
                      idx === existingIndex ? modifierObj : m,
                    );

            updated[groupIndex] = { ...group, modifiers: newModifiers };
          } else {
            // Remove modifier
            let newModifiers = group.modifiers.filter(
              (_, idx) => idx !== existingIndex,
            );

            if (newModifiers.length === 0) {
              // remove group entirely
              updated = updated.filter((_, idx) => idx !== groupIndex);
            } else {
              updated[groupIndex] = { ...group, modifiers: newModifiers };
            }
          }

          return updated;
        }

        // CASE 2 — group NOT found
        if (isChecked) {
          return [
            ...updated,
            {
              g_id: modifierGroup.g_id,
              g_name: modifierGroup.g_name,
              g_parent_id: modifierGroup.g_parent_id,
              modifiers: [modifierObj],
            },
          ];
        }

        return updated;
      });
    },
    [],
  );

  const validateModifiers = () => {
    let isValid = true;
    const newErrors: ModifierErrors = {};

    product.modifier_group
      .filter((modifierGroup) =>
        isParentModifierSelected(modifierGroup.g_parent_id),
      )
      .forEach((modifierGroup) => {
        const { g_min_selection, g_name, g_id } = modifierGroup;

        // Validate required modifier groups
        if (g_min_selection > 0) {
          const selectedModifiersCount =
            selectedModifiers
              .find((group) => group.g_id === g_id)
              ?.modifiers.reduce(
                (total, selectedMod) => total + (selectedMod.m_qty || 1),
                0,
              ) ?? 0;

          if (selectedModifiersCount < g_min_selection) {
            isValid = false;
            newErrors[g_id] = {
              g_name,
              message: `Validation failed for modifier group: ${g_name}. Minimum required: ${g_min_selection}, selected: ${selectedModifiersCount}`,
            };
          }
        }
      });

    setErrors(newErrors);
    return { isValid, errors: newErrors };
  };

  const handleClick = () => {
    const validation = validateModifiers();
    if (!validation.isValid) {
      Toast.error("Please select mandatory options");

      // Scroll to the first error
      const firstErrorId = Object.keys(validation.errors)[0];
      if (firstErrorId) {
        scrollToError(firstErrorId);
      }

      return;
    }

    if (isEditMode && cartProduct && typeof index === "number") {
      updateCartItem(index, {
        ...cartProduct,
        product_qty: quantity,
        product_instruction: instructions,
        modifier_group: selectedModifiers,
      });
    } else {
      addToCart(product, quantity, selectedModifiers, instructions, menuId);

      // Toast.info("Item added to cart");
    }

    handleSubmit();
  };

  const isParentModifierSelected = (g_parent_id: string) => {
    if (g_parent_id === "0") return true;

    return selectedModifiers.some((group) =>
      group.modifiers.some((modifier) => modifier.m_id === g_parent_id),
    );
  };

  const productPrice = useProductPrice(product, selectedModifiers, quantity);
  const productBasePrice = useProductPrice(product);

  const inflationContext = useMemo(
    () => ({
      typeId: orderOption.type_id,
      hasAdInflation,
      productPrice: Number(product.product_price),
      productBasePrice,
    }),
    [orderOption.type_id, hasAdInflation, productPrice, productBasePrice],
  );

  // Memoize filtered modifier groups to avoid recalculation on every render
  const visibleModifierGroups = useMemo(
    () =>
      product.modifier_group.filter((modifierGroup) =>
        isParentModifierSelected(modifierGroup.g_parent_id),
      ),
    [product.modifier_group, selectedModifiers],
  );

  // Memoize cross-sell products
  const crossProducts = useMemo(
    () =>
      storeProducts.filter((item) =>
        product.crossell_products.includes(item.id),
      ),
    [storeProducts, product.crossell_products],
  );

  const isPaidModifierSelected = selectedModifiers.some((group) =>
    group.modifiers.some((modifier) => modifier.m_price > 0),
  );

  // const applicableCoupons = useProductCoupons(product);

  return (
    <>
      {/* <Modal
        className="cart_aad_on app-modal"
        show={show}
        onHide={handleClose}
        fullscreen="sm-down"
        centered
      >
        <Modal.Header closeButton className="app-modal-header" />
        <Modal.Body className="app-modal-body">
          <div className="product-img-container">
            <div style={productImageStyle}  />
          </div>
          <div className="modal-common-padding">
             <div className="peppy_hrd">
            <h3>{product.product_name}</h3>
            {product.product_desc && <p>{product.product_desc}</p>}

            <h4>
              {(Number(product.product_price) > 0 ||
                isPaidModifierSelected) && <>{formatCurrency(productPrice)}</>}

              {product.coupon && (
                <>
                  {" "}
                  <span className="coupon-code mx-1">
                    {product.coupon.title}
                  </span>
                </>
              )}
            </h4>
          </div>

          {visibleModifierGroups.map((modifierGroup, index) => {
            const props = {
              modifierGroup,
              selectedModifiers,
              handleSelectModifier,
            };

            const setModifierRef = (el: HTMLDivElement | null) => {
              modifierRefs.current[modifierGroup.g_id] = el;
            };

            switch (modifierGroup.g_type_id) {
              case ModifierGroupTypeEnum.Tab:
                return (
                  <div
                    key={index}
                    ref={setModifierRef}
                    className={
                      errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                    }
                  >
                    <TabModifierGroup
                      {...props}
                      index={index}
                      inflationContext={inflationContext}
                      errors={errors}
                    />
                  </div>
                );
              case ModifierGroupTypeEnum.Quantity:
                return (
                  <div
                    key={index}
                    ref={setModifierRef}
                    className={
                      errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                    }
                  >
                    <QuantityModifierGroup
                      {...props}
                      index={index}
                      inflationContext={inflationContext}
                      errors={errors}
                    />
                  </div>
                );
              case ModifierGroupTypeEnum.Custom:
                return (
                  <div
                    key={index}
                    ref={setModifierRef}
                    className={
                      errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                    }
                  >
                    <CustomModifierGroup
                      {...props}
                      index={index}
                      inflationContext={inflationContext}
                      errors={errors}
                    />
                  </div>
                );
              case ModifierGroupTypeEnum.Regular:
                return (
                  <div
                    key={index}
                    ref={setModifierRef}
                    className={
                      errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                    }
                  >
                    <CommonModifierGroup
                      {...props}
                      index={index}
                      inflationContext={inflationContext}
                      errors={errors}
                    />
                  </div>
                );
              default:
                return (
                  <div
                    key={index}
                    ref={setModifierRef}
                    className={
                      errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                    }
                  >
                    <CommonModifierGroup
                      {...props}
                      index={index}
                      inflationContext={inflationContext}
                      errors={errors}
                    />
                  </div>
                );
            }
          })}

          {crossProducts.length > 0 && (
            <CrossSellProduct products={crossProducts} />
          )}

          <div className="special_meal_sec">
            <h3>Special Instructions <span className="text-muted fw-semibold">(Optional)</span> </h3>
            <textarea
              name="instructions"
              placeholder="Share your instruction if any."
              value={instructions}
              onInput={(e: any) => setInstructions(e.target.value)}
            ></textarea>
          </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="app-modal-footer">
          <div className="button_meel_bottom d-flex justify-content-between align-items-center">
            <span className="d-flex align-items-center product-addon">
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent"
                onClick={() => setQuantity((prev) => prev - 1)}
                disabled={quantity == 1}
              >
                <PiMinusCircleBold size={22} className="plusminus-icon" />
              </button>

              <span
                className="mx-2 px-3 py-1 rounded fw-bold text-center"
                style={{
                  backgroundColor: "#f7f7f7",
                  minWidth: "40px",
                  fontSize: "16px",
                }}
              >
                {quantity}
              </span>

              <button
                type="button"
                className="btn p-0 border-0 bg-transparent"
                onClick={() => setQuantity((prev) => prev + 1)}
              >
                <PiPlusCircleBold size={22} className="plusminus-icon" />
              </button>
            </span>
            <button type="button" onClick={handleClick}>
              {isEditMode ? "UPDATE" : "ADD TO CART"}
              {(Number(product.product_price) > 0 ||
                isPaidModifierSelected) && (
                <>
                  {" - "} {formatCurrency(productPrice)}
                </>
              )}
            </button>
          </div>
        </Modal.Footer>
      </Modal> */}

      {/* Second Modal Layout Design */}
      <Modal
        className="cart_aad_on app-modal"
        show={show}
        onHide={handleClose}
        fullscreen="sm-down"
        centered
      >
        <Modal.Header closeButton className="app-modal-header" />
        <Modal.Body className="app-modal-body">
          {/* Top row — image + product info side by side */}
          <div className="d-flex flex-row modal-common-padding gap-3 product-modal-top-row">
            {/* {product?.product_image && (
              <div className="product-img-container" style={{ flex: "0 0 40%", maxWidth: "40%" }}>
                <div style={productImageStyle} />
              </div>
            )} */}
            {product?.product_image && (
              <div
                className="product-img-container"
                style={{ flex: "0 0 40%", maxWidth: "40%" }}
              >
                <Zoom>
                  <img
                    src={product.product_image}
                    alt={product.product_name}
                    style={{
                      width: "100%",
                      height: "150px",
                      objectFit: "cover",
                    }}
                  />
                </Zoom>
              </div>
            )}
            <div
              style={{ flex: product?.product_image ? "0 0 60%" : "1 1 100%" }}
            >
              <div
                className={`peppy_hrd ${product?.product_image ? "px-4" : ""}`}
              >
                <h3>{product.product_name}</h3>
                {product.product_desc && <p>{product.product_desc}</p>}
                <h4>
                  {(Number(product.product_price) > 0 ||
                    isPaidModifierSelected) && (
                    <>{formatCurrency(productPrice)}</>
                  )}
                  {product.coupon && (
                    <>
                      {" "}
                      <span className="coupon-code mx-1">
                        {product.coupon.title}
                      </span>
                    </>
                  )}
                </h4>
              </div>
            </div>
          </div>

          {/* Bottom section — modifiers + special instructions full width */}
          <div className="modal-common-padding pt-0">
            {visibleModifierGroups.map((modifierGroup, index) => {
              const props = {
                modifierGroup,
                selectedModifiers,
                handleSelectModifier,
              };
              const setModifierRef = (el: HTMLDivElement | null) => {
                modifierRefs.current[modifierGroup.g_id] = el;
              };

              switch (modifierGroup.g_type_id) {
                case ModifierGroupTypeEnum.Tab:
                  return (
                    <div
                      key={index}
                      ref={setModifierRef}
                      className={
                        errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                      }
                    >
                      <TabModifierGroup
                        {...props}
                        index={index}
                        inflationContext={inflationContext}
                        errors={errors}
                      />
                    </div>
                  );
                case ModifierGroupTypeEnum.Quantity:
                  return (
                    <div
                      key={index}
                      ref={setModifierRef}
                      className={
                        errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                      }
                    >
                      <QuantityModifierGroup
                        {...props}
                        index={index}
                        inflationContext={inflationContext}
                        errors={errors}
                      />
                    </div>
                  );
                case ModifierGroupTypeEnum.Custom:
                  return (
                    <div
                      key={index}
                      ref={setModifierRef}
                      className={
                        errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                      }
                    >
                      <CustomModifierGroup
                        {...props}
                        index={index}
                        inflationContext={inflationContext}
                        errors={errors}
                      />
                    </div>
                  );
                case ModifierGroupTypeEnum.Regular:
                default:
                  return (
                    <div
                      key={index}
                      ref={setModifierRef}
                      className={
                        errors[modifierGroup.g_id] ? "error-modifier-group" : ""
                      }
                    >
                      <CommonModifierGroup
                        {...props}
                        index={index}
                        inflationContext={inflationContext}
                        errors={errors}
                      />
                    </div>
                  );
              }
            })}

            {crossProducts.length > 0 && (
              <CrossSellProduct products={crossProducts} />
            )}

            <div className="special_meal_sec">
              <h3>
                Special Instructions{" "}
                <span className="text-muted fw-semibold">(Optional)</span>
              </h3>
              <textarea
                name="instructions"
                placeholder="Share your instruction if any."
                value={instructions}
                onInput={(e: any) => setInstructions(e.target.value)}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="app-modal-footer">
          <div className="button_meel_bottom d-flex justify-content-between align-items-center">
            <span className="d-flex align-items-center product-addon">
              <button
                type="button"
                className="btn p-0 border-0 bg-transparent"
                onClick={() => setQuantity((prev) => prev - 1)}
                disabled={quantity == 1}
              >
                <PiMinusCircleBold size={22} className="plusminus-icon" />
              </button>

              <span
                className="mx-2 px-3 py-1 rounded fw-bold text-center"
                style={{
                  backgroundColor: "#f7f7f7",
                  minWidth: "40px",
                  fontSize: "16px",
                }}
              >
                {quantity}
              </span>

              <button
                type="button"
                className="btn p-0 border-0 bg-transparent"
                disabled={quantity >= MAX_PRODUCT_QUANTITY}
                onClick={() => setQuantity((prev) => prev + 1)}
              >
                <PiPlusCircleBold size={22} className="plusminus-icon" />
              </button>
            </span>
            <button type="button" onClick={handleClick}>
              {isEditMode ? "UPDATE" : "ADD TO CART"}
              {(Number(product.product_price) > 0 ||
                isPaidModifierSelected) && (
                <>
                  {" - "} {formatCurrency(productPrice)}
                </>
              )}
            </button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* End */}
    </>
  );
};

export default ProductAddOnModal;
